import * as THREE from "three";
import Experience from "./Experience.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

export default class Camera {
    constructor() {
        this.experience = new Experience();
        this.sizes = this.experience.sizes;
        this.scene = this.experience.scene;
        this.canvas = this.experience.canvas;

        this.setInstance();
        this.setControls();

        // Views
        this.views = [
            {
                name: "Default View",
                position: new THREE.Vector3(0.14, 2.55, 2.4),
                target: new THREE.Vector3(0.14, 2.55, 0),
            },
            {
                name: "Dining Table",
                position: new THREE.Vector3(2.4, 2.55, -2.7),
                target: new THREE.Vector3(0, 2.55, -2.7),
            },
            {
                name: "Stairs",
                position: new THREE.Vector3(-3, 2.55, 0.685),
                target: new THREE.Vector3(-0.31, 3.27, -3.29),
            },
            {
                name: "Kitchen",
                position: new THREE.Vector3(-2.0, 2.55, -3),
                target: new THREE.Vector3(0, 2.55, -3),
            },
        ];

        this.currentViewIndex = 0;
        this.targetPosition = this.views[0].position.clone();
        this.targetControlTarget = this.views[0].target.clone();
        this.isAnimating = false;

        // Precalculate right and up vectors for each view
        const worldUp = new THREE.Vector3(0, 1, 0);
        this.views.forEach((view) => {
            const dir = new THREE.Vector3()
                .copy(view.target)
                .sub(view.position)
                .normalize();
            view.right = new THREE.Vector3()
                .crossVectors(dir, worldUp)
                .normalize();
            view.up = new THREE.Vector3()
                .crossVectors(view.right, dir)
                .normalize();
        });

        // Base attributes for smoothing
        this.basePosition = this.views[0].position.clone();
        this.baseTarget = this.views[0].target.clone();
        this.baseRight = this.views[0].right.clone();
        this.baseUp = this.views[0].up.clone();

        // Targets for smoothing
        this.targetRight = this.views[0].right.clone();
        this.targetUp = this.views[0].up.clone();

        // Mouse
        this.mouse = new THREE.Vector2();
        this.smoothedMouse = new THREE.Vector2();

        this.onMouseMove = (event) => {
            this.mouse.x = event.clientX / this.sizes.width - 0.5;
            this.mouse.y = event.clientY / this.sizes.height - 0.5;
        };

        window.addEventListener("mousemove", this.onMouseMove);
    }

    setInstance() {
        this.instance = new THREE.PerspectiveCamera(
            65,
            this.sizes.width / this.sizes.height,
            0.1,
            100,
        );
        this.instance.position.set(0.14, 2.55, 2.4);
        this.scene.add(this.instance);
    }

    setControls() {
        this.controls = new OrbitControls(this.instance, this.canvas);
        this.controls.target.set(0.14, 2.55, 0);
        this.controls.enableDamping = true;
    }

    resize() {
        this.instance.aspect = this.sizes.width / this.sizes.height;
        this.instance.updateProjectionMatrix();
    }

    changeView(direction) {
        if (direction === "next") {
            this.currentViewIndex =
                (this.currentViewIndex + 1) % this.views.length;
        } else if (direction === "prev") {
            this.currentViewIndex =
                (this.currentViewIndex - 1 + this.views.length) %
                this.views.length;
        }

        const view = this.views[this.currentViewIndex];
        this.targetPosition.copy(view.position);
        this.targetControlTarget.copy(view.target);
        this.targetRight.copy(view.right);
        this.targetUp.copy(view.up);
        this.isAnimating = true;

        return view.name;
    }

    update() {
        // Smoothly move base attributes
        this.basePosition.lerp(this.targetPosition, 0.05);
        this.baseTarget.lerp(this.targetControlTarget, 0.05);
        this.baseRight.lerp(this.targetRight, 0.05);
        this.baseUp.lerp(this.targetUp, 0.05);

        // Check if we are close enough to stop "animating" flag
        if (this.isAnimating) {
            if (
                this.basePosition.distanceTo(this.targetPosition) < 0.01 &&
                this.baseTarget.distanceTo(this.targetControlTarget) < 0.01
            ) {
                this.isAnimating = false;
                this.basePosition.copy(this.targetPosition);
                this.baseTarget.copy(this.targetControlTarget);
                this.baseRight.copy(this.targetRight);
                this.baseUp.copy(this.targetUp);
            }
        }

        // Parallax
        this.smoothedMouse.lerp(this.mouse, 0.05);

        const parallaxX = this.smoothedMouse.x * 0.2;
        const parallaxY = -this.smoothedMouse.y * 0.2;

        // Apply base position + parallax using precalculated/interpolated axes
        this.instance.position
            .copy(this.basePosition)
            .addScaledVector(this.baseRight, parallaxX)
            .addScaledVector(this.baseUp, parallaxY);

        // Apply base target to controls
        this.controls.target.copy(this.baseTarget);

        this.controls.update();
    }

    destroy() {
        this.controls.dispose();
        window.removeEventListener("mousemove", this.onMouseMove);
    }
}
